import React from 'react';
import { makeStyles } from '@mui/styles';
import { Box, Typography, Button } from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CaseMasterSignupDialog from 'dialogs/CaseMasterSignupDialog';

// Serve through CloudFront rather than hitting the S3 bucket directly.
const ASSETS_URL = (process.env.REACT_APP_ASSETS_URL || 'https://languify-assets-v02.s3.ap-south-1.amazonaws.com')
  .replace(/\/$/, '');

const DEMO_VIDEO_URL = `${ASSETS_URL}/video/Case_Master_AI_Homepage.mp4`;

const useStyles = makeStyles((theme) => ({
  container: {
    width: '100%',
    // Matches the top of the CaseSolutions gradient that follows this section,
    // so the two read as one continuous dark band.
    background: '#0A3F63',
    padding: theme.spacing(20, 37.5),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    [theme.breakpoints.down('xl')]: {
      padding: theme.spacing(20, 15),
    },
    [theme.breakpoints.down('lg')]: {
      padding: theme.spacing(15, 15),
    },
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(12, 10),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(10, 5),
    },
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    width: 'fit-content',
    border: `1px solid ${theme.palette.shades['clr-white-900']}`,
    borderRadius: '20px',
    padding: theme.spacing(1.5, 4),
    marginBottom: theme.spacing(6),
    // 10px / 600 / uppercase with 1px tracking — matches the badge in the design.
    ...theme.typography['overline-semiBold'],
    color: theme.palette.shades['clr-white-900'],
  },
  heading: {
    ...theme.typography['h2-bold'],
    color: theme.palette.shades['clr-white-900'],
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('lg')]: {
      ...theme.typography['h3-bold'],
    },
    [theme.breakpoints.down('md')]: {
      ...theme.typography['h4-bold'],
      marginBottom: theme.spacing(3),
    },
  },
  subHeading: {
    ...theme.typography['body01-regular'],
    color: theme.palette.shades['clr-white-900'],
    maxWidth: '620px',
    lineHeight: 1.7,
    marginBottom: theme.spacing(15),
    [theme.breakpoints.down('md')]: {
      ...theme.typography['body02-regular'],
      lineHeight: 1.7,
      marginBottom: theme.spacing(10),
    },
  },
  videoWrapper: {
    width: '100%',
    maxWidth: '760px',
    marginBottom: theme.spacing(20),
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(12),
    },
  },
  video: {
    display: 'block',
    width: '100%',
    height: 'auto',
    borderRadius: '16px',
    backgroundColor: '#000000',
    outline: 'none',
  },
  featureRow: {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(4),
    marginBottom: theme.spacing(10),
    [theme.breakpoints.down('md')]: {
      gap: theme.spacing(3),
      marginBottom: theme.spacing(8),
    },
  },
  featureChip: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    backgroundColor: theme.palette.shades['clr-white-900'],
    border: '1px solid #E4E4E7',
    borderRadius: '8px',
    padding: theme.spacing(3, 5),
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2.5, 4),
    },
  },
  featureIcon: {
    color: '#0051FF',
    fontSize: '20px',
    flexShrink: 0,
  },
  featureText: {
    ...theme.typography['body02-semiBold'],
    color: theme.palette.neutral['clr-900'],
    whiteSpace: 'nowrap',
  },
  ctaButton: {
    backgroundColor: '#184EFF',
    color: theme.palette.shades['clr-white-900'],
    ...theme.typography['body01-semiBold'],
    padding: theme.spacing(3, 8),
    borderRadius: '8px',
    textTransform: 'none',
    transition: 'background-color 0.3s ease',
    '&:hover': {
      backgroundColor: '#0F3ACC',
    },
  },
}));

// The four steps of the flow shown in the demo, in order.
const demoSteps = [
  { id: 'case', label: 'Choose a Real-World Case', Icon: DescriptionOutlinedIcon },
  { id: 'practise', label: 'Practise with AI', Icon: SmartToyOutlinedIcon },
  { id: 'feedback', label: 'Get Instant Feedback', Icon: ChatBubbleOutlineIcon },
  { id: 'track', label: 'Track Progress & Reattempt', Icon: TrendingUpIcon },
];

const CaseProductDemo = () => {
  const classes = useStyles();

  const [openSignupDialog, setOpenSignupDialog] = React.useState(false);

  return (
    <Box className={classes.container}>
      <Box className={classes.badge}>Product Demo</Box>

      <Typography className={classes.heading}>
        See Case Master AI in Action
      </Typography>

      <Typography className={classes.subHeading}>
        Choose from 150+ real interview cases, practice with an AI interviewer,
        receive detailed feedback, track your progress, and reattempt until you&rsquo;re ready.
      </Typography>

      <Box className={classes.videoWrapper}>
        <video
          className={classes.video}
          src={DEMO_VIDEO_URL}
          controls
          playsInline
          preload="metadata"
        />
      </Box>

      <Box className={classes.featureRow}>
        {demoSteps.map(({ id, label, Icon }) => (
          <Box key={id} className={classes.featureChip}>
            <Icon className={classes.featureIcon} />
            <Typography className={classes.featureText}>{label}</Typography>
          </Box>
        ))}
      </Box>

      <Button
        className={classes.ctaButton}
        onClick={() => setOpenSignupDialog(true)}
      >
        Practice Case For Free
      </Button>

      <CaseMasterSignupDialog
        open={openSignupDialog}
        onClose={() => setOpenSignupDialog(false)}
      />
    </Box>
  );
};

export default CaseProductDemo;
